import React, { useState } from 'react';
import { Search, ChevronDown, CreditCard, DollarSign, TrendingUp, TrendingDown, AlertCircle, Check, X, Eye, RefreshCw } from 'lucide-react';
import '../../../assests/css/admin/Payments/Payments.css';

const PaymentCustomer = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState('last30');
  const [statusFilter, setStatusFilter] = useState('all');
  const [methodFilter, setMethodFilter] = useState('all');
  const [showAlert, setShowAlert] = useState(false);
  const [alertConfig, setAlertConfig] = useState({ type: '', message: '', title: '' });
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);

  const [paymentsData, setPaymentsData] = useState([
    {
      id: '#TRX-9982',
      date: 'Oct 24, 2023',
      time: '10:42 AM',
      customer: 'Jane Doe',
      email: 'jane@example.com',
      avatar: 'JD',
      amount: '$120.50',
      method: 'Mastercard',
      methodIcon: 'card',
      cardLast4: '4242',
      status: 'completed'
    },
    {
      id: '#TRX-9981',
      date: 'Oct 24, 2023',
      time: '09:15 AM',
      customer: 'John Davis',
      email: 'john.d@example.com',
      avatar: 'JD',
      amount: '$349.00',
      method: 'PayPal',
      methodIcon: 'paypal',
      status: 'pending'
    },
    {
      id: '#TRX-9980',
      date: 'Oct 23, 2023',
      time: '4:30 PM',
      customer: 'Sarah Wilson',
      email: 'swilson@test.com',
      avatar: 'SW',
      amount: '$42.00',
      method: 'Visa',
      methodIcon: 'card',
      cardLast4: '1892',
      status: 'failed'
    },
    {
      id: '#TRX-9979',
      date: 'Oct 23, 2023',
      time: '1:20 PM',
      customer: 'Mike Brown',
      email: 'mike.b@work.com',
      avatar: 'MB',
      amount: '$1,250.00',
      method: 'Bank Transfer',
      methodIcon: 'bank',
      status: 'completed'
    },
    {
      id: '#TRX-9978',
      date: 'Oct 22, 2023',
      time: '11:00 AM',
      customer: 'Emily Clarke',
      email: 'emily.c@example.com',
      avatar: 'EC',
      amount: '$85.00',
      method: 'Visa',
      methodIcon: 'card',
      cardLast4: '5521',
      status: 'refunded'
    }
  ]);

  // Stats calculation
  const totalRevenue = paymentsData
    .filter(p => p.status === 'completed')
    .reduce((sum, p) => sum + parseFloat(p.amount.replace(/[$,]/g, '')), 0);
  
  const pendingCount = paymentsData.filter(p => p.status === 'pending').length;
  
  const refundedAmount = paymentsData
    .filter(p => p.status === 'refunded')
    .reduce((sum, p) => sum + parseFloat(p.amount.replace(/[$,]/g, '')), 0);

  const showAlertMessage = (type, title, message) => {
    setAlertConfig({ type, title, message });
    setShowAlert(true);
    setTimeout(() => setShowAlert(false), 3000);
  };

  const handleViewTransaction = (payment) => {
    setSelectedPayment(payment);
    setShowDetailModal(true);
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedPayment(null);
  };

  const getStatusClass = (status) => {
    const classes = {
      completed: 'Payments-Admin-payments-status-completed',
      pending: 'Payments-Admin-payments-status-pending',
      failed: 'Payments-Admin-payments-status-failed',
      refunded: 'Payments-Admin-payments-status-refunded'
    };
    return classes[status] || '';
  };

  const getMethodIcon = (method) => {
    if (method.toLowerCase().includes('card') || method.toLowerCase().includes('visa') || method.toLowerCase().includes('mastercard')) {
      return <CreditCard size={16} />;
    }
    return <DollarSign size={16} />;
  };

  const filteredPayments = paymentsData.filter(payment => {
    const matchesSearch = payment.customer.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         payment.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         payment.id.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || payment.status === statusFilter;
    const matchesMethod = methodFilter === 'all' || payment.method.toLowerCase().includes(methodFilter.toLowerCase());
    
    return matchesSearch && matchesStatus && matchesMethod;
  });

  return (
    <div className="Payments-Admin-payments-container">
      {/* Alert Message */}
      {showAlert && (
        <div className={`Payments-Admin-payments-alert-message Payments-Admin-payments-${alertConfig.type}`}>
          <div className="Payments-Admin-payments-alert-icon">
            {alertConfig.type === 'success' && <Check size={24} />}
            {alertConfig.type === 'error' && <X size={24} />}
            {alertConfig.type === 'info' && <AlertCircle size={24} />}
          </div>
          <div className="Payments-Admin-payments-alert-content">
            <h3>{alertConfig.title}</h3>
            <p>{alertConfig.message}</p>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedPayment && (
        <div className="Payments-Admin-payments-modal-overlay" onClick={closeDetailModal}>
          <div className="Payments-Admin-payments-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="Payments-Admin-payments-modal-header">
              <h2>Transaction Details</h2>
              <button className="Payments-Admin-payments-modal-close" onClick={closeDetailModal}>
                <X size={24} />
              </button>
            </div>
            <div className="Payments-Admin-payments-modal-body">
              <div className="Payments-Admin-payments-detail-row">
                <span className="Payments-Admin-payments-detail-label">Transaction ID</span>
                <span className="Payments-Admin-payments-detail-value">{selectedPayment.id}</span>
              </div>
              <div className="Payments-Admin-payments-detail-row">
                <span className="Payments-Admin-payments-detail-label">Date & Time</span>
                <span className="Payments-Admin-payments-detail-value">{selectedPayment.date} at {selectedPayment.time}</span>
              </div>
              <div className="Payments-Admin-payments-detail-row">
                <span className="Payments-Admin-payments-detail-label">Customer Name</span>
                <span className="Payments-Admin-payments-detail-value">{selectedPayment.customer}</span>
              </div>
              <div className="Payments-Admin-payments-detail-row">
                <span className="Payments-Admin-payments-detail-label">Customer Email</span>
                <span className="Payments-Admin-payments-detail-value">{selectedPayment.email}</span>
              </div>
              <div className="Payments-Admin-payments-detail-row">
                <span className="Payments-Admin-payments-detail-label">Amount</span>
                <span className="Payments-Admin-payments-detail-value Payments-Admin-payments-amount-highlight">{selectedPayment.amount}</span>
              </div>
              <div className="Payments-Admin-payments-detail-row">
                <span className="Payments-Admin-payments-detail-label">Payment Method</span>
                <span className="Payments-Admin-payments-detail-value">
                  {selectedPayment.method}
                  {selectedPayment.cardLast4 && ` (**** ${selectedPayment.cardLast4})`}
                </span>
              </div>
              <div className="Payments-Admin-payments-detail-row">
                <span className="Payments-Admin-payments-detail-label">Status</span>
                <span className={`Payments-Admin-payments-status-badge ${getStatusClass(selectedPayment.status)}`}>
                  <span className="Payments-Admin-payments-status-dot"></span>
                  {selectedPayment.status.charAt(0).toUpperCase() + selectedPayment.status.slice(1)}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="Payments-Admin-payments-header">
        <h1 className="Payments-Admin-payments-page-title">Payments Overview</h1>
      </div>

      {/* Stats Cards */}
      <div className="Payments-Admin-payments-stats-grid">
        <div className="Payments-Admin-payments-stat-card Payments-Admin-payments-stat-revenue">
          <div className="Payments-Admin-payments-stat-icon Payments-Admin-payments-revenue-icon">
            <DollarSign size={24} />
          </div>
          <div className="Payments-Admin-payments-stat-content">
            <p className="Payments-Admin-payments-stat-label">TOTAL REVENUE</p>
            <h2 className="Payments-Admin-payments-stat-value">${totalRevenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</h2>
            <div className="Payments-Admin-payments-stat-change Payments-Admin-payments-positive">
              <TrendingUp size={14} />
              <span>12%</span>
            </div>
            <p className="Payments-Admin-payments-stat-subtitle">Compared to last month</p>
          </div>
        </div>

        <div className="Payments-Admin-payments-stat-card Payments-Admin-payments-stat-pending">
          <div className="Payments-Admin-payments-stat-icon Payments-Admin-payments-pending-icon">
            <AlertCircle size={24} />
          </div>
          <div className="Payments-Admin-payments-stat-content">
            <p className="Payments-Admin-payments-stat-label">PENDING PAYMENTS</p>
            <h2 className="Payments-Admin-payments-stat-value">{pendingCount}</h2>
            <div className="Payments-Admin-payments-stat-change Payments-Admin-payments-positive">
              <TrendingUp size={14} />
              <span>2%</span>
            </div>
            <p className="Payments-Admin-payments-stat-subtitle">Requires attention</p>
          </div>
        </div>

        <div className="Payments-Admin-payments-stat-card Payments-Admin-payments-stat-refunded">
          <div className="Payments-Admin-payments-stat-icon Payments-Admin-payments-refunded-icon">
            <RefreshCw size={24} />
          </div>
          <div className="Payments-Admin-payments-stat-content">
            <p className="Payments-Admin-payments-stat-label">REFUNDED AMOUNT</p>
            <h2 className="Payments-Admin-payments-stat-value">${refundedAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</h2>
            <div className="Payments-Admin-payments-stat-change Payments-Admin-payments-negative">
              <TrendingDown size={14} />
              <span>0.5%</span>
            </div>
            <p className="Payments-Admin-payments-stat-subtitle">From total revenue</p>
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <div className="Payments-Admin-payments-filters-section">
        <div className="Payments-Admin-payments-search-box">
          <Search className="Payments-Admin-payments-search-icon" size={20} />
          <input
            type="text"
            placeholder="Search by Order ID or Customer..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="Payments-Admin-payments-filter-group">
          <div className="Payments-Admin-payments-filter-dropdown">
            <span>Date: Last 30 Days</span>
            <ChevronDown size={18} />
            <select value={dateFilter} onChange={(e) => setDateFilter(e.target.value)}>
              <option value="today">Today</option>
              <option value="last7">Last 7 Days</option>
              <option value="last30">Last 30 Days</option>
              <option value="last90">Last 90 Days</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>

          <div className="Payments-Admin-payments-filter-dropdown">
            <span>Status: All</span>
            <ChevronDown size={18} />
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">All</option>
              <option value="completed">Completed</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
              <option value="refunded">Refunded</option>
            </select>
          </div>

          <div className="Payments-Admin-payments-filter-dropdown">
            <span>Method: All</span>
            <ChevronDown size={18} />
            <select value={methodFilter} onChange={(e) => setMethodFilter(e.target.value)}>
              <option value="all">All</option>
              <option value="card">Card</option>
              <option value="paypal">PayPal</option>
              <option value="bank">Bank Transfer</option>
            </select>
          </div>
        </div>
      </div>

      {/* Payments Table */}
      <div className="Payments-Admin-payments-table">
        <table>
          <thead>
            <tr>
              <th>TRANSACTION ID</th>
              <th>DATE & TIME</th>
              <th>CUSTOMER</th>
              <th>AMOUNT</th>
              <th>PAYMENT METHOD</th>
              <th>STATUS</th>
              <th>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {filteredPayments.length > 0 ? (
              filteredPayments.map((payment, index) => (
                <tr key={payment.id} style={{ animationDelay: `${index * 0.05}s` }}>
                  <td>
                    <span className="Payments-Admin-payments-transaction-id">{payment.id}</span>
                  </td>
                  <td>
                    <div className="Payments-Admin-payments-date-time">
                      <span className="Payments-Admin-payments-date">{payment.date}</span>
                      <span className="Payments-Admin-payments-time">{payment.time}</span>
                    </div>
                  </td>
                  <td>
                    <div className="Payments-Admin-payments-customer-cell">
                      <div className="Payments-Admin-payments-customer-avatar">{payment.avatar}</div>
                      <div className="Payments-Admin-payments-customer-info">
                        <span className="Payments-Admin-payments-customer-name">{payment.customer}</span>
                        <span className="Payments-Admin-payments-customer-email">{payment.email}</span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className="Payments-Admin-payments-amount">{payment.amount}</span>
                  </td>
                  <td>
                    <div className="Payments-Admin-payments-payment-method">
                      <div className="Payments-Admin-payments-method-icon">
                        {getMethodIcon(payment.method)}
                      </div>
                      <div className="Payments-Admin-payments-method-info">
                        <span className="Payments-Admin-payments-method-name">{payment.method}</span>
                        {payment.cardLast4 && (
                          <span className="Payments-Admin-payments-card-number">**** {payment.cardLast4}</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={`Payments-Admin-payments-status-badge ${getStatusClass(payment.status)}`}>
                      <span className="Payments-Admin-payments-status-dot"></span>
                      {payment.status.charAt(0).toUpperCase() + payment.status.slice(1)}
                    </span>
                  </td>
                  <td>
                    <div className="Payments-Admin-payments-action-menu">
                      <button 
                        className="Payments-Admin-payments-action-btn Payments-Admin-payments-view-btn" 
                        onClick={() => handleViewTransaction(payment)}
                        title="View Details"
                      >
                        <Eye size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="Payments-Admin-payments-no-data">
                  <p>No payments found</p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PaymentCustomer;